import { useState, useCallback } from 'react'
import { cn } from '../utils/cn'

interface SliderInputProps {
  value: number
  onChange: (value: number) => void
  min: number
  max: number
  step?: number
  presets?: number[]
  label: string
  description?: string
  unit?: string
  formatValue?: (value: number) => string
  className?: string
}

export function SliderInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  presets = [],
  label,
  description,
  unit = '',
  formatValue,
  className,
}: SliderInputProps) {
  const [isDragging, setIsDragging] = useState(false)

  const handleSliderChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = Math.max(min, Math.min(max, parseInt(event.target.value)))
      onChange(newValue)
    },
    [min, max, onChange]
  )

  const handlePresetClick = useCallback(
    (preset: number) => {
      onChange(preset)
    },
    [onChange]
  )

  const displayValue = formatValue ? formatValue(value) : `${value}${unit ? ` ${unit}` : ''}`

  // Calculate percentage for visual feedback
  const percentage = ((value - min) / (max - min)) * 100

  return (
    <div className={cn('space-y-3', className)}>
      {/* Label and current value */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        <div className="flex items-center space-x-2">
          <span className="text-base font-semibold text-orange-600">{displayValue}</span>
        </div>
      </div>

      {/* Description */}
      {description && <p className="text-sm text-gray-600 -mt-1">{description}</p>}

      {/* Slider container */}
      <div className="relative pt-2 pb-8">
        {/* Custom slider track */}
        <div className="relative h-2 bg-gray-200 rounded-full">
          {/* Progress fill - orange/amber gradient */}
          <div
            className="absolute h-full bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 rounded-full transition-all duration-200"
            style={{ width: `${percentage}%` }}
          />

          {/* Slider handle */}
          <div
            className={cn(
              'absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white border-2 border-orange-500 rounded-full shadow-lg transition-all duration-200',
              isDragging ? 'scale-110 shadow-xl ring-2 ring-orange-300' : 'hover:scale-105'
            )}
            style={{ left: `calc(${percentage}% - 10px)` }}
          />
        </div>

        {/* Hidden native slider */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleSliderChange}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={() => setIsDragging(false)}
          onTouchStart={() => setIsDragging(true)}
          onTouchEnd={() => setIsDragging(false)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          aria-label={label}
        />

        {/* Tick marks */}
        <div className="absolute top-full mt-1.5 w-full flex justify-between text-xs text-gray-400">
          <span>
            {min}
            {unit}
          </span>
          <span>
            {Math.round((min + max) / 2)}
            {unit}
          </span>
          <span>
            {max}
            {unit}
          </span>
        </div>
      </div>

      {/* Preset buttons */}
      {presets.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-gray-500 mr-1 self-center">Quick select:</span>
          {presets.map((preset) => (
            <button
              key={preset}
              onClick={() => handlePresetClick(preset)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md border transition-all duration-200',
                value === preset
                  ? 'bg-orange-100 border-orange-300 text-orange-800 shadow-sm'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
              )}
              aria-pressed={value === preset}
            >
              {preset}
              {unit}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
